import { useEffect, useMemo } from 'react'
import { useTransactionDetails } from 'modules/blockChain/hooks/useTransactionDetails'
import { getNativeTokenLabel } from 'modules/pools/utils/getNativeTokenLabel'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'

import { InfoFieldValue } from 'shared/ui/common/InfoFieldValue'

import { PoolAsset } from 'modules/pools/constants/poolAssets'
import { FormattedDate } from 'shared/ui/utils/FormattedDate'
import { ContractSuccessTitle } from 'shared/ui/common/ContractSuccessTitle'

import { formatAsset } from 'modules/pools/utils/formatAsset'
import { useLatestPriceUSD } from 'modules/blockChain/hooks/useLatestPriceUSD'
import { useFirebase } from 'modules/analytics/firebase'
import {
  BORROW_FAIL_EVENT,
  BORROW_SUCCESS_EVENT,
  COLLATERAL_FAIL_EVENT,
  COLLATERAL_SUCCESS_EVENT,
  DEPOSIT_FAIL_EVENT,
  DEPOSIT_SUCCESS_EVENT,
  DEPOSIT_WITHDRAWAL_FAIL_EVENT,
  DEPOSIT_WITHDRAWAL_SUCCESS_EVENT,
  REPAYMENT_FAIL_EVENT,
  REPAYMENT_SUCCESS_EVENT,
} from 'modules/analytics/events'

import s from './SentTransaction.module.scss'

type Props = {
  txHash: string
  transactionType: string
  amountToDisplay?: string
  amountToken?: PoolAsset | ''
  stillInProgress?: boolean
  warning?: React.ReactNode
  onSuccess?: () => void
  onFail?: () => void
}

export function SentTransaction({
  txHash,
  transactionType,
  amountToken = '',
  amountToDisplay,
  stillInProgress,
  warning,
  onSuccess,
  onFail,
}: Props) {
  const { chainId } = useWeb3()
  const { trackEvent } = useFirebase()
  const { block, txStatus, txGasCost } = useTransactionDetails({
    hash: txHash,
    defaultStatus: 'pending',
    onSuccess,
    onFail,
  })

  const nativeTokenLabel = getNativeTokenLabel(chainId)
  const amountUsd = useLatestPriceUSD(amountToken, Number(amountToDisplay ?? 0))
  // TODO: use cost of a native token
  // const gasCostUsd = useLatestPriceUSD(nativeTokenLabel, txGasCost)

  useEffect(() => {
    if (txStatus.isSuccess) {
      switch (transactionType) {
        case 'Borrow':
          trackEvent(BORROW_SUCCESS_EVENT)
          break
        case 'Deposit':
          trackEvent(DEPOSIT_SUCCESS_EVENT)
          break
        case 'Collateral refill':
          trackEvent(COLLATERAL_SUCCESS_EVENT, { action: 'add' })
          break
        case 'Collateral withdrawal':
          trackEvent(COLLATERAL_SUCCESS_EVENT, { action: 'withdraw' })
          break
        case 'Repayment':
          trackEvent(REPAYMENT_SUCCESS_EVENT)
          break
        case 'Deposit withdrawal':
          trackEvent(DEPOSIT_WITHDRAWAL_SUCCESS_EVENT)
          break
        default:
          break
      }
    } else if (txStatus.isFailed) {
      switch (transactionType) {
        case 'Borrow':
          trackEvent(BORROW_FAIL_EVENT)
          break
        case 'Deposit':
          trackEvent(DEPOSIT_FAIL_EVENT)
          break
        case 'Collateral refill':
          trackEvent(COLLATERAL_FAIL_EVENT, { action: 'add' })
          break
        case 'Collateral withdrawal':
          trackEvent(COLLATERAL_FAIL_EVENT, { action: 'withdraw' })
          break
        case 'Repayment':
          trackEvent(REPAYMENT_FAIL_EVENT)
          break
        case 'Deposit withdrawal':
          trackEvent(DEPOSIT_WITHDRAWAL_FAIL_EVENT)
          break
        default:
          break
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txStatus.status])

  const textFromStatus = useMemo(() => {
    if (txStatus.isFailed) {
      return (
        <>
          has <br />
          failed
        </>
      )
    }

    if (txStatus.isPending || stillInProgress) {
      return (
        <>
          in <br />
          progress
        </>
      )
    }

    if (txStatus.isSuccess) {
      return (
        <>
          <br />
          successfull
        </>
      )
    }
  }, [
    txStatus.isFailed,
    txStatus.isPending,
    txStatus.isSuccess,
    stillInProgress,
  ])

  return (
    <div className={s.wrap}>
      <ContractSuccessTitle
        status={
          txStatus.isSuccess && stillInProgress ? 'pending' : txStatus.status
        }
        txHash={txHash}
      >
        {transactionType} {textFromStatus}
      </ContractSuccessTitle>
      {txStatus.isSuccess && warning}
      <div className={s.row}>
        <InfoFieldValue
          label="Type of transaction"
          value={transactionType}
          className={s.column}
        />
        <InfoFieldValue
          label="Time"
          value={
            block ? (
              <FormattedDate
                format="DD-MM-YYYY hh:mm a"
                date={block.timestamp}
              />
            ) : (
              'pending...'
            )
          }
          className={s.column}
        />
      </div>
      <div className={s.row}>
        <InfoFieldValue
          label="Amount"
          value={
            amountToDisplay
              ? formatAsset(amountToDisplay, amountToken)
              : 'pending...'
          }
          sign={amountToDisplay ? amountToken : undefined}
          tooltip={amountToDisplay}
          secondaryValue={amountUsd ? `(${amountUsd.toFixed(2)} USD)` : null}
          className={s.column}
        />
        <InfoFieldValue
          label="Gas cost"
          value={
            txGasCost ? formatAsset(txGasCost, nativeTokenLabel) : 'pending...'
          }
          sign={txGasCost ? nativeTokenLabel : undefined}
          tooltip={txGasCost.toFixed(18)}
          // secondaryValue={gasCostUsd ? `(${gasCostUsd.toFixed(2)} USD)` : null}
          className={s.column}
        />
      </div>
    </div>
  )
}
